import { STORAGE_KEYS } from './constants';
import type { ISession, RegistrationNumber } from './types';

/**
 * Thin wrapper over localStorage. Access goes through here so swapping the
 * store (cookie, IndexedDB, in-memory for SSR) touches one file, and so a
 * disabled/full storage never throws through the auth flow.
 *
 * The whole session record is stored, not just the token: survey-api's token is
 * opaque, so `expiresAt` and `kind` are only knowable because login returned
 * them — dropping them would leave nothing to grade the session by.
 */
export const sessionStore = {
  get(): Nullable<ISession> {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.SESSION);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<ISession>;
      // A record written by an older build (or hand-edited) is treated as
      // absent rather than half-trusted.
      return parsed?.token && parsed?.expiresAt ? (parsed as ISession) : null;
    } catch {
      return null;
    }
  },

  set(session: ISession) {
    try {
      localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(session));
    } catch (error) {
      console.warn('[Auth] Failed to persist session:', error);
    }
  },

  remove() {
    try {
      localStorage.removeItem(STORAGE_KEYS.SESSION);
    } catch (error) {
      console.warn('[Auth] Failed to clear session:', error);
    }
  }
};

/** What the HTTP client puts in the Authorization header. */
export const tokenStorage = {
  get(): Nullable<string> {
    return sessionStore.get()?.token ?? null;
  }
};

/**
 * Where to land after a successful login. Stashed when a guard bounces an
 * unauthenticated visit, cleared as soon as it is consumed so a later manual
 * login doesn't reuse a stale path.
 */
export const redirectStorage = {
  get(): Nullable<string> {
    try {
      return sessionStorage.getItem(STORAGE_KEYS.REDIRECT_URL);
    } catch {
      return null;
    }
  },

  set(url: string) {
    try {
      sessionStorage.setItem(STORAGE_KEYS.REDIRECT_URL, url);
    } catch (error) {
      console.warn('[Auth] Failed to stash redirect URL:', error);
    }
  },

  take(): Nullable<string> {
    const value = redirectStorage.get();
    try {
      sessionStorage.removeItem(STORAGE_KEYS.REDIRECT_URL);
    } catch (error) {
      console.warn('[Auth] Failed to clear stashed redirect URL:', error);
    }
    return value;
  }
};

/**
 * Which account said "Not now" to the address confirmation prompt, in this tab.
 *
 * `sessionStorage`, for the reason a registration run is kept there: the answer belongs to the
 * tab that gave it, survives a reload and dies with the tab — so the prompt is back the next
 * time the portal is opened, which is the "not now" a person meant. Keyed by account, so a
 * second account signed into the same tab is asked for itself. A convenience and nothing more:
 * storage that throws or is empty only means the prompt shows.
 */
export const verificationPromptStorage = {
  isDismissed(accountId: string): boolean {
    try {
      return sessionStorage.getItem(STORAGE_KEYS.VERIFICATION_PROMPT_DISMISSED) === accountId;
    } catch {
      return false;
    }
  },

  dismiss(accountId: string) {
    try {
      sessionStorage.setItem(STORAGE_KEYS.VERIFICATION_PROMPT_DISMISSED, accountId);
    } catch (error) {
      console.warn('[Auth] Failed to keep the prompt dismissed:', error);
    }
  }
};

/** survey-api's run ids are GUIDs (`{runId:guid}` on every route). Checked on the way out of
 *  storage rather than trusted, for `sessionStore`'s reason: a value this build did not write
 *  is absent, and this one is spliced into a request path. */
const RUN_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The registration run this tab is walking, so a reload resumes it instead of starting over —
 * one store per caller: `registrationRunStorage(null)` for the anonymous page, and
 * `registrationRunStorage(accountId)` for a run bound to that account (`/portal/surveys/add`).
 *
 * **In `sessionStorage` and never in the URL.** survey-api calls a run id "a bearer of
 * nothing", and for reading one that holds — but a run parked on `complete` after a confirmed
 * link to a participant we already hold can be finished by whoever has the id, with their own
 * address, and the account that mints speaks for that participant's responses. So it is a
 * credential for one step, and it gets a mailed token's treatment: `docker/nginx.conf` logs
 * in the default `combined` format, where a path or a query would land. `sessionStorage`
 * rather than `localStorage` because a run belongs to the tab that started it — it survives a
 * reload and dies with the tab, and a second tab starts a registration of its own.
 *
 * **Keyed by the account, apart from the anonymous key** (the teams onboarding plan's R7). A
 * bound run is the account's: resumed under another account — signed out and in again in the
 * same tab — or on the anonymous page, it would be an empty 404 at best, and an anonymous run
 * resumed with a bearer would bind nothing it was meant to. So each caller reads only its own,
 * and a run somebody else's session reaches anyway is the page's "we could not find it".
 *
 * **What the run is for is kept beside its id.** A person's run keeps its declared numbers
 * (SR-487), so Back to page 01 keeps its ticks, and each number as it was entered — "Is this
 * you?" and "You already have an account" name it, and survey-api's view does not carry it, so a
 * reload on those pages would otherwise lose it. A number is no secret here: a CRD is public,
 * and whoever holds the tab typed it. A firm's or a team's keeps the survey it started
 * on (SR-416), because survey-api's read of a redirected run is a 409 that names no destination,
 * and a run's one redirect is fixed by the survey it started on — the tab that comes back to one
 * says where to go from this alone.
 *
 * **The anonymous page holds a person's run alone**: since SR-487 an anonymous survey run answers
 * 404, so a `{runId, survey}` or a bare id an older build stored there is forgotten. An
 * account's store keeps either kind, a bare id with its survey unknown.
 */
export function registrationRunStorage(accountId: Nullable<string>) {
  const key = registrationRunKey(accountId);

  return {
    get(): Nullable<IHeldRun> {
      try {
        const run = heldRun(sessionStorage.getItem(key));
        return accountId === null && run?.numbers === null ? null : run;
      } catch {
        return null;
      }
    },

    set(run: IHeldRun) {
      try {
        sessionStorage.setItem(key, JSON.stringify(run));
      } catch (error) {
        console.warn('[Auth] Failed to keep the registration run:', error);
      }
    },

    remove() {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        console.warn('[Auth] Failed to clear the registration run:', error);
      }
    }
  };
}

export type RegistrationRunStore = ReturnType<typeof registrationRunStorage>;

/** A run a tab holds: its id, and what it is for — a person's declared numbers, or the survey a
 *  firm's or a team's started on. Each null where the run is the other kind, both for an id an
 *  older build stored bare. `entered` is a person's run's numbers as last submitted, by number. */
export interface IHeldRun {
  runId: string;
  survey: Nullable<string>;
  numbers: Nullable<RegistrationNumber[]>;
  entered?: EnteredNumbers;
}

/** A number as the registrant submitted it at its anchor: the value, the type it was sent as
 *  (`CRD`, `NPN`, `INSURANCE_LICENSE`) and a licence's state. */
export interface IEnteredNumber {
  value: string;
  idType: string;
  issuingState?: string;
}

export type EnteredNumbers = Partial<Record<RegistrationNumber, IEnteredNumber>>;

const REGISTRATION_NUMBERS: readonly string[] = ['crd', 'insurance'] satisfies RegistrationNumber[];

/** The stored `entered`, each number kept only in the shape `set` writes it. */
function enteredNumbers(stored: unknown): EnteredNumbers {
  if (typeof stored !== 'object' || stored === null) return {};
  const entered: EnteredNumbers = {};
  for (const [number, held] of Object.entries(stored)) {
    if (!REGISTRATION_NUMBERS.includes(number) || typeof held !== 'object' || held === null) {
      continue;
    }
    const { value, idType, issuingState } = held as Record<string, unknown>;
    if (typeof value !== 'string' || typeof idType !== 'string') continue;
    entered[number as RegistrationNumber] =
      typeof issuingState === 'string' ? { value, idType, issuingState } : { value, idType };
  }
  return entered;
}

/** What a stored value is, if it is a run at all: `{runId, numbers}` or `{runId, survey}` as
 *  `set` writes them, or a bare id. Anything else — an id that is no GUID, a number this build
 *  does not know — is absent. */
function heldRun(stored: Nullable<string>): Nullable<IHeldRun> {
  if (!stored) return null;
  if (RUN_ID.test(stored)) return { runId: stored, survey: null, numbers: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { runId, survey, numbers, entered } = parsed as Record<string, unknown>;
  if (typeof runId !== 'string' || !RUN_ID.test(runId)) return null;
  if (numbers !== undefined && numbers !== null) {
    const known =
      Array.isArray(numbers) &&
      numbers.every((number) => REGISTRATION_NUMBERS.includes(number as string));
    if (!known) return null;
  }
  return {
    runId,
    survey: typeof survey === 'string' ? survey : null,
    numbers: Array.isArray(numbers) ? (numbers as RegistrationNumber[]) : null,
    entered: enteredNumbers(entered)
  };
}

/** The `sessionStorage` key a caller's run is kept under — the anonymous page's, or one per
 *  account. Exported for the E2E suite, which reads a tab's run where the page keeps it. */
export function registrationRunKey(accountId: Nullable<string> | undefined): string {
  return accountId
    ? `${STORAGE_KEYS.REGISTRATION_RUN}.${accountId}`
    : STORAGE_KEYS.REGISTRATION_RUN;
}
